<template>
    <div>
        <div class="card box_component mx-auto">
            <h5 class="card-header">{{ $t('trick_list.card_header') }}</h5>
            <div class="card-body px-0">
                <div class="px-3 pt-3">
                    <TrickVideoCarousel
                        :has-carousel="hasCarousel"
                        :title="$t('trick_list.latest_videos') || 'Ultimos videos publicados'"
                        :items="visibleCarouselItems"
                        :item-style="carouselItemStyle"
                        :is-mobile="isMobile"
                        @prev="prevCarousel"
                        @next="nextCarousel"
                        @select-trick="openTrickFromCarousel"
                    />
                </div>

                <div ref="privateRecommendationSection" class="px-3 pt-2">
                    <TrickRecommendationPrivateCard
                        ref="privateRecommendationCard"
                        :recommendations="privateRecommendations"
                        :tricklist-version="recommendationVersion"
                        :tricks="yourData"
                        :categories="selectorCategories.length ? selectorCategories : categories"
                        :levels="selectorLevels.length ? selectorLevels : levels"
                        :types="selectorTypes"
                        :loading="recommendationLoading"
                        :saving="recommendationSaving"
                        :error="recommendationPrivateError"
                        :deleting-id="recommendationDeletingId"
                        :labels="recommendationLabels"
                        @submit="submitRecommendation"
                        @delete="deleteRecommendation"
                    />
                </div>

                <div class="trick-layout px-3 pb-4">
                    <div class="trick-charts-panel">
                        <TrickSummaryCharts
                            ref="summaryCharts"
                            :level-total="chartLevelTotal"
                            :category-total="chartCategoryTotal"
                            :has-level-data="chartLevelStats.length > 0"
                            :has-category-data="chartCategoryStats.length > 0"
                            :labels="chartLabels"
                        />
                    </div>

                    <div class="trick-list-panel">
                        <TrickTablePanel
                            :paginated-data="paginatedData"
                            :search-query="searchQuery"
                            :filter-category="filterCategory"
                            :filter-level="filterLevel"
                            :categories="categories"
                            :levels="levels"
                            :current-page="currentPage"
                            :total-pages="totalPages"
                            :limited-pages="limitedPages"
                            :sort-orders="sortOrders"
                            :is-supervisor-user="isSupervisorUser"
                            :category-name="categoryName"
                            :get-background-color-class="getBackgroundColorClass"
                            :labels="tableLabels"
                            @update:searchQuery="searchQuery = $event"
                            @update:filterCategory="filterCategory = $event"
                            @update:filterLevel="filterLevel = $event"
                            @sort-by="sortBy"
                            @change-page="changePage"
                            @reset-filter="resetFilter"
                            @select-trick="viewDetails"
                            @create-trick="openCreateModal"
                            @open-csv-sync="openCsvSyncView"
                        />
                    </div>
                </div>

                <div class="px-3 pb-4">
                    <TrickRecommendationPublicCard
                        :tricklist-version="recommendationVersion"
                        :summary="publicRecommendationSummary"
                        :recommendations="publicRecommendations"
                        :loading="recommendationPublicLoading"
                        :error="recommendationPublicError"
                        :vote-busy-id="recommendationVoteBusyId"
                        :labels="recommendationLabels"
                        @open-detail="openPublicRecommendationDetail"
                        @vote="voteRecommendation"
                    />
                </div>

                <div class="modal fade" id="publicRecommendationDetailModal" tabindex="-1" aria-labelledby="publicRecommendationDetailModalLabel" aria-hidden="true">
                    <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                        <div class="modal-content" v-if="selectedPublicRecommendation">
                            <div class="modal-header">
                                <div>
                                    <p class="public-recommendation-modal__eyebrow mb-1">{{ isEditRecommendation(selectedPublicRecommendation) ? recommendationLabels.editType : recommendationLabels.newType }}</p>
                                    <h5 class="modal-title" id="publicRecommendationDetailModalLabel">{{ selectedPublicRecommendation.proposed_name || selectedPublicRecommendation.trick?.name || recommendationLabels.untitled }}</h5>
                                </div>
                                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div class="modal-body public-recommendation-modal__body">
                                <div class="public-recommendation-modal__meta">
                                    <span>{{ recommendationLabels.version }}: {{ recommendationVersion?.name || recommendationVersion?.id || recommendationLabels.notSpecified }}</span>
                                    <span>{{ recommendationLabels.createdAt }}: {{ formatRecommendationDate(selectedPublicRecommendation?.created_at) }}</span>
                                </div>

                                <div v-if="isEditRecommendation(selectedPublicRecommendation)" class="public-recommendation-modal__section">
                                    <h6>{{ recommendationLabels.changeSummary }}</h6>
                                    <div v-if="publicRecommendationChangedFields(selectedPublicRecommendation).length" class="public-recommendation-modal__changes">
                                        <article v-for="change in publicRecommendationChangedFields(selectedPublicRecommendation)" :key="change.field" class="public-recommendation-modal__change-card">
                                            <span class="public-recommendation-modal__change-label">{{ change.label }}</span>
                                            <div class="public-recommendation-modal__change-values">
                                                <div>
                                                    <small>{{ recommendationLabels.originalValue }}</small>
                                                    <strong>{{ formatRecommendationFieldValue(change.old_display) }}</strong>
                                                </div>
                                                <div class="public-recommendation-modal__change-arrow">→</div>
                                                <div>
                                                    <small>{{ recommendationLabels.proposedValue }}</small>
                                                    <strong>{{ formatRecommendationFieldValue(change.new_display) }}</strong>
                                                </div>
                                            </div>
                                        </article>
                                    </div>
                                    <div v-else class="recommendation-empty py-3">{{ recommendationLabels.noChangesDetected }}</div>
                                </div>

                                <div v-else class="public-recommendation-modal__section">
                                    <h6>{{ recommendationLabels.proposedData }}</h6>
                                    <dl class="public-recommendation-modal__grid">
                                        <div v-for="field in publicRecommendationDetailFields(selectedPublicRecommendation)" :key="field.label">
                                            <dt>{{ field.label }}</dt>
                                            <dd>{{ field.value }}</dd>
                                        </div>
                                    </dl>
                                </div>

                                <div class="public-recommendation-modal__section">
                                    <h6>{{ recommendationLabels.detail }}</h6>
                                    <p class="mb-0">{{ selectedPublicRecommendation.detail || recommendationLabels.notSpecified }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal fade" id="infoModal" tabindex="-1" aria-labelledby="infoModalLabel" aria-hidden="true">
                    <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h1 class="modal-title fs-5" id="infoModalLabel">{{ $t('trick_list.infoModal.title') }}</h1>
                                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div class="modal-body">
                                <div class="info-row">
                                    <div class="label">{{ $t('trick_list.name') }}:</div>
                                    <div class="value">{{ selectedTrick.name }}</div>
                                </div>
                                <div v-if="selectedTrick.short_name" class="info-row">
                                    <div class="label">{{ $t('trick_list.details') }}:</div>
                                    <div class="value">{{ selectedTrick.short_name }}</div>
                                </div>
                                <div class="info-row">
                                    <div class="label">{{ $t('trick_list.value') }}:</div>
                                    <div class="value">{{ selectedTrick.value }}</div>
                                </div>
                                <div class="info-row">
                                    <div class="label justify-content-between d-flex align-items-center">{{ $t('trick_list.level') }}:
                                        <div class="level-circle text-center mr-1" :class="getBackgroundColorClass(selectedTrick.level_id)">
                                            <b>{{ selectedTrick.level_id }}</b>
                                        </div>
                                    </div>
                                    <div class="value d-flex align-items-center">
                                        {{ capitalizeFirstLetter(selectedTrick) }}
                                    </div>
                                </div>
                                <div class="info-row">
                                    <div class="label">{{ $t('trick_list.category') }}:</div>
                                    <div class="value">{{ categoryName(selectedTrick.categorie_id) }}</div>
                                </div>
                                <div class="info-row info-row--action">
                                    <button type="button" class="btn btn-outline-primary btn-sm" @click="openRecommendationFromSelectedTrick">
                                        <i class="fa-solid fa-wand-magic-sparkles me-2"></i>{{ recommendationLabels.recommendFromTrick }}
                                    </button>
                                </div>
                                <div class="info-row flex-column w-100">
                                    <div class="label mb-2 d-flex justify-content-between align-items-center">
                                        <span>Video:</span>
                                        <div v-if="hasRotationToggle" class="btn-group btn-group-sm" role="group">
                                            <button type="button" class="btn btn-outline-secondary" :class="{ active: selectedRotation === 1 }" @click.stop="selectRotation(1)">Frontside</button>
                                            <button type="button" class="btn btn-outline-secondary" :class="{ active: selectedRotation === 2 }" @click.stop="selectRotation(2)">Backside</button>
                                        </div>
                                    </div>
                                    <div class="value w-100">
                                        <template v-if="selectedVideoUrl">
                                            <YoutubePlayerSmall
                                                :key="selectedVideoUrl"
                                                :video-url="selectedVideoUrl"
                                                :video-id="selectedVideoId"
                                                :allow-delete="isSupervisorUser"
                                                :show-timer="false"
                                                @delete-video-url="requestDeleteVideo()"
                                            />
                                        </template>
                                        <template v-else-if="videos.length === 0 && isSupervisorUser">
                                            <p>{{ $t('trick_list.videoInsertModal.message') }}</p>
                                            <small class="d-block mb-2"><b>Formatos: mp4, mkv, avi. Se sube en partes de ~5MB.</b></small>
                                            <div class="form-group mb-2">
                                                <label class="form-label">Rotación</label>
                                                <select class="form-select" v-model="uploadRotation">
                                                    <option :value="null">Ninguna</option>
                                                    <option :value="1">Frontside</option>
                                                    <option :value="2">Backside</option>
                                                </select>
                                            </div>
                                            <div class="form-group my-3">
                                                <input class="form-control" type="file" accept="video/mp4,video/x-matroska,video/avi,video/*" @change="onFileSelected" />
                                            </div>
                                            <div v-if="uploading" class="progress mb-2">
                                                <div class="progress-bar" role="progressbar" :style="{ width: uploadProgress + '%' }" :aria-valuenow="uploadProgress" aria-valuemin="0" aria-valuemax="100">{{ uploadProgress }}%</div>
                                            </div>
                                            <div class="d-flex justify-content-end gap-2">
                                                <button class="btn btn-info" :disabled="!uploadFile || uploading" @click.prevent="uploadVideo">{{ uploading ? uploadProgress + '%' : $t('trick_list.videoInsertModal.btn') }}</button>
                                            </div>
                                            <p v-if="uploadSuccess" class="text-success mt-2">{{ uploadSuccess }}</p>
                                            <p v-if="uploadError" class="text-danger mt-2">{{ uploadError }}</p>
                                        </template>
                                        <template v-else-if="selectedVideoUrl === null && hasRotationToggle">
                                            <div v-if="isSupervisorUser">
                                                <p class="mb-1">{{ missingRotation === 1 ? 'Falta Frontside' : 'Falta Backside' }}</p>
                                                <small class="d-block mb-2"><b>Formatos: mp4, mkv, avi. Se sube en partes de ~5MB.</b></small>
                                                <div class="form-group my-3">
                                                    <input class="form-control" type="file" accept="video/mp4,video/x-matroska,video/avi,video/*" @change="onFileSelected" />
                                                </div>
                                                <div v-if="uploading" class="progress mb-2">
                                                    <div class="progress-bar" role="progressbar" :style="{ width: uploadProgress + '%' }" :aria-valuenow="uploadProgress" aria-valuemin="0" aria-valuemax="100">{{ uploadProgress }}%</div>
                                                </div>
                                                <div class="d-flex justify-content-end gap-2">
                                                    <button class="btn btn-info" :disabled="!uploadFile || uploading" @click.prevent="uploadVideo">{{ uploading ? uploadProgress + '%' : $t('trick_list.videoInsertModal.btn') }}</button>
                                                </div>
                                                <p v-if="uploadSuccess" class="text-success mt-2">{{ uploadSuccess }}</p>
                                                <p v-if="uploadError" class="text-danger mt-2">{{ uploadError }}</p>
                                            </div>
                                            <div v-else>
                                                <img :src="videoOffImage" alt="Video no disponible" class="img-fluid" />
                                            </div>
                                        </template>
                                        <template v-else-if="videos.length === 0 && !isSupervisorUser">
                                            <img :src="videoOffImage" alt="Video no disponible" class="img-fluid" />
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal fade" id="videoModal" tabindex="-1" aria-labelledby="videoModalLabel" aria-hidden="true">
                    <div class="modal-dialog modal-dialog-centered modal-lg">
                        <div class="modal-content">
                            <div class="modal-body" style="padding: 0;">
                                <YoutubePlayerSmall
                                    v-if="youtubeLink"
                                    :video-url="youtubeLink"
                                    :video-id="selectedVideoId"
                                    :allow-delete="isSupervisorUser"
                                    :show-timer="false"
                                    @delete-video-url="requestDeleteVideo()"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal fade" id="createTrickModal" tabindex="-1" aria-labelledby="createTrickModalLabel" aria-hidden="true">
                    <div class="modal-dialog">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h5 class="modal-title" id="createTrickModalLabel">{{ $t('trick_list.createModal.title') || 'Crear truco' }}</h5>
                                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div class="modal-body">
                                <div class="mb-3">
                                    <label class="form-label">{{ $t('trick_list.createModal.name') || 'Nombre' }}</label>
                                    <input type="text" class="form-control text-capitalize" v-model="createForm.name" />
                                </div>
                                <div class="mb-3">
                                    <label class="form-label">{{ $t('trick_list.createModal.short_name') || 'Nombre técnico' }}</label>
                                    <input type="text" class="form-control" v-model="createForm.short_name" />
                                </div>
                                <div class="mb-3">
                                    <label class="form-label">{{ $t('trick_list.createModal.description') || 'Descripción' }}</label>
                                    <textarea class="form-control" rows="3" v-model="createForm.description"></textarea>
                                </div>
                                <div class="row small-row">
                                    <div class="col-12 col-md-4 mb-3">
                                        <label class="form-label">{{ $t('trick_list.createModal.category') || 'Categoría' }}</label>
                                        <select class="form-select form-select-sm" v-model="createForm.categorie_id">
                                            <option :value="null">--</option>
                                            <option v-for="cat in selectorCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                                        </select>
                                    </div>
                                    <div class="col-12 col-md-4 mb-3">
                                        <label class="form-label">{{ $t('trick_list.createModal.level') || 'Nivel' }}</label>
                                        <select class="form-select form-select-sm" v-model="createForm.level_id">
                                            <option :value="null">--</option>
                                            <option v-for="lvl in selectorLevels" :key="lvl.id" :value="lvl.id">{{ lvl.name }}</option>
                                        </select>
                                    </div>
                                    <div class="col-12 col-md-4 mb-3">
                                        <label class="form-label">{{ $t('trick_list.createModal.end_type') || 'Finaliza a' }}</label>
                                        <select class="form-select form-select-sm" v-model="createForm.type_id">
                                            <option :value="null">--</option>
                                            <option v-for="type in selectorTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                                        </select>
                                    </div>
                                    <div class="col-12 col-md-4 mb-3">
                                        <label class="form-label">{{ $t('trick_list.createModal.value') || 'Valor' }}</label>
                                        <input type="number" min="0" class="form-control form-control-sm" v-model.number="createForm.value" />
                                    </div>
                                </div>
                                <div v-if="createError" class="alert alert-danger py-2">{{ createError }}</div>
                            </div>
                            <div class="modal-footer">
                                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('trick_list.createModal.cancel') || 'Cancelar' }}</button>
                                <button type="button" class="btn btn-primary" :disabled="createLoading" @click.prevent="createTrick">
                                    <span v-if="createLoading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {{ $t('trick_list.createModal.save') || 'Guardar' }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <loading-modal ref="loadingModal"></loading-modal>

        <div class="modal fade" id="confirmDeleteVideoModal" tabindex="-1" aria-labelledby="confirmDeleteVideoModalLabel" aria-hidden="true">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="confirmDeleteVideoModalLabel">{{ $t('trick_list.deleteModal.title') }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-2">{{ $t('trick_list.deleteModal.message') }}</p>
                        <p v-if="deleteError" class="text-danger mb-0">{{ deleteError }}</p>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('trick_list.deleteModal.cancel') }}</button>
                        <button type="button" class="btn btn-danger" :disabled="deleteLoading" @click="confirmDeleteVideo">{{ deleteLoading ? $t('trick_list.deleteModal.deleting') : $t('trick_list.deleteModal.confirm') }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';
import YoutubePlayerSmall from "@/services/YoutubePlayerSmall.vue";
import LoadingModal from "@/services/LoadingModal.vue";
import TrickSummaryCharts from "@/components/trick-list/TrickSummaryCharts.vue";
import TrickTablePanel from "@/components/trick-list/TrickTablePanel.vue";
import TrickVideoCarousel from "@/components/trick-list/TrickVideoCarousel.vue";
import TrickRecommendationPrivateCard from "@/components/trick-list/TrickRecommendationPrivateCard.vue";
import TrickRecommendationPublicCard from "@/components/trick-list/TrickRecommendationPublicCard.vue";
import {secureStorage} from "@/services/secureStorage";
import { mapState } from "vuex";
import { isSupervisor } from "@/helpers/roleUtils";
import { Modal } from "bootstrap";

export default {
    name: "TrickListBox",
    components: {
        TrickSummaryCharts,
        TrickTablePanel,
        TrickVideoCarousel,
        TrickRecommendationPrivateCard,
        TrickRecommendationPublicCard,
        YoutubePlayerSmall,
        LoadingModal
    },
    data() {
        return {
            yourData: [],
            currentPage: 1,
            pageSize: 20,
            sortKey: "",
            sortOrders: {
                id: 1,
                name: 1,
                value: 1,
                level: 1,
                category: 1,
            },
            searchQuery: "",
            selectedTrick: {
                name: '',
                value: 0,
                level: '',
                category: '',
            },
            isMobile: window.innerWidth < 576,
            nameModal: "#videoInsertModal",
            youtubeLink: null,
            embedCode: '',
            trickId: null,
            videoOffImage: require('@/assets/videoOff.jpg'),
            filterCategory: '',
            filterLevel: '',
            categories : [
                { id: 1, name: 'Aerial'},
                { id: 2, name: 'Aerial / Flip'},
                { id: 3, name: 'Aerial / Switch'},
                { id: 4, name: 'Dynamic'},
                { id: 5, name: 'Dynamic / Flip'},
                { id: 6, name: 'Dynamic / Flip / Switch '},
                { id: 7, name: 'Dynamic / Switch'},
                { id: 8, name: 'Dynamic / Transition'},
                { id: 9, name: 'Mount'},
                { id: 10, name: 'Static'},
                { id: 11, name: 'Transition'},
            ],
            levels: [
                { id: 1, name: 'ROOKY'},
                { id: 2, name: 'AMATEUR'},
                { id: 3, name: 'PRO'},
                { id: 4, name: 'LEGENDARY'},
            ],
            uploadFile: null,
            uploadError: '',
            uploading: false,
            uploadProgress: 0,
            totalChunks: 0,
            uploadId: null,
            CHUNK_SIZE: 5 * 1024 * 1024,
            uploadSuccess: '',
            selectedRotation: null,
            uploadRotation: null,
            carouselIndex: 0,
            autoSlideTimer: null,
            autoSlideDelay: 10000,
            selectorCategories: [],
            selectorLevels: [],
            selectorTypes: [],
            createForm: {
                name: '',
                short_name: '',
                description: '',
                categorie_id: null,
                level_id: null,
                value: null,
                type_id: null,
            },
            createError: '',
            createLoading: false,
            videoToDelete: null,
            deleteError: '',
            deleteLoading: false,
            trickLevelSummaryChartInstance: null,
            trickCategorySummaryChartInstance: null,
            summaryChartRenderNonce: 0,
            recommendationVersion: null,
            recommendationContext: {
                round_id: null,
                competition_id: null,
                tricklist_version_id: null,
            },
            privateRecommendations: [],
            publicRecommendations: [],
            publicRecommendationSummary: {
                total: 0,
                contributors: 0,
                by_type: [],
                by_status: [],
                by_category: [],
                by_level: [],
            },
            recommendationLoading: false,
            recommendationPublicLoading: false,
            recommendationSaving: false,
            recommendationDeletingId: null,
            recommendationVoteBusyId: null,
            recommendationPrivateError: '',
            recommendationPublicError: '',
            selectedPublicRecommendation: null,
        };
    },
    mounted() {
        this.yourData = JSON.parse(secureStorage.getItem('tricks')) || [];
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('tricks:updated', this.handleRealtimeTricklistUpdate);
        document.getElementById('videoModal').addEventListener('hidden.bs.modal', this.onModalHidden);
        this.scheduleAutoSlide();
        this.renderSummaryCharts();
        this.fetchSelectors();
        this.initializeRecommendationCards();
    },
    beforeUnmount() {
        if (this.autoSlideTimer) {
            clearTimeout(this.autoSlideTimer);
        }
        this.destroySummaryCharts();
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('tricks:updated', this.handleRealtimeTricklistUpdate);
    },
    computed: {
        ...mapState('user', ['user','token']),
        totalPages() {
            const filteredList = this.filteredData || [];
            return Math.ceil(filteredList.length / this.pageSize);
        },
        paginatedData() {
            const start = (this.currentPage - 1) * this.pageSize;
            const end = start + this.pageSize;
            return this.filteredData.slice(start, end);
        },
        filteredData() {
            const query = (this.searchQuery || '').toLowerCase().trim();
            return this.yourData
                .slice()
                .filter((trick) => {
                    const name = (trick.name || '').toLowerCase();
                    const shortName = (trick.short_name || '').toLowerCase();
                    const idStr = trick.id !== undefined && trick.id !== null ? String(trick.id).toLowerCase() : '';
                    const matchesSearch = !query || name.includes(query) || shortName.includes(query) || idStr.includes(query);
                    return matchesSearch &&
                        (this.filterCategory === '' || trick.categorie_id === this.filterCategory) &&
                        (this.filterLevel === '' || trick.level_id === this.filterLevel);
                })
                .sort(this.sortData);
        },
        limitedPages() {
            const totalVisiblePages = 5;
            const middlePage = Math.floor(totalVisiblePages / 2);
            let startPage = this.currentPage - middlePage;
            let endPage = this.currentPage + middlePage;

            if (startPage < 1) {
                startPage = 1;
                endPage = totalVisiblePages;
            }

            if (endPage > this.totalPages) {
                endPage = this.totalPages;
                startPage = Math.max(1, endPage - totalVisiblePages + 1);
            }

            return Array.from(
                { length: endPage - startPage + 1 },
                (_, index) => startPage + index
            );
        },
        isSupervisorUser() {
            return isSupervisor(this.user);
        },
        videos() {
            return Array.isArray(this.selectedTrick?.videos) ? this.selectedTrick.videos : [];
        },
        activeVideoTricks() {
            return (this.yourData || [])
                .filter(t => Array.isArray(t.videos) && t.videos.length > 0)
                .map(trick => ({ trick, latest: this.getLatestVideoDate(trick) }))
                .sort((a, b) => b.latest - a.latest)
                .map(item => item.trick);
        },
        frontVideo() {
            return this.videos.find(v => v.rotation === 1);
        },
        backVideo() {
            return this.videos.find(v => v.rotation === 2);
        },
        singleVideo() {
            return this.videos.find(v => v.rotation === null || v.rotation === undefined);
        },
        hasRotationToggle() {
            return !!(this.frontVideo || this.backVideo);
        },
        hasCarousel() {
            return this.activeVideoTricks.length > 0;
        },
        missingRotation() {
            if (this.frontVideo && !this.backVideo) return 2;
            if (this.backVideo && !this.frontVideo) return 1;
            return null;
        },
        selectedVideoUrl() {
            if (this.hasRotationToggle) {
                if (this.selectedRotation === 1) return this.frontVideo?.video_url || null;
                if (this.selectedRotation === 2) return this.backVideo?.video_url || null;
            }
            return this.singleVideo?.video_url || null;
        },
        selectedVideo() {
            if (this.hasRotationToggle) {
                if (this.selectedRotation === 1) return this.frontVideo || null;
                if (this.selectedRotation === 2) return this.backVideo || null;
            }
            const direct = this.singleVideo || this.frontVideo || this.backVideo || null;
            if (direct) return direct;
            if (this.youtubeLink && Array.isArray(this.videos)) {
                return this.videos.find(v => v.video_url === this.youtubeLink) || null;
            }
            return null;
        },
        selectedVideoId() {
            const vid = this.selectedVideo;
            if (!vid) return null;
            return vid.id || vid.video_id || null;
        },
        carouselVisibleCount() {
            return this.isMobile ? 1 : 3;
        },
        visibleCarouselTricks() {
            const items = this.activeVideoTricks;
            const count = this.carouselVisibleCount;
            if (items.length <= count) return items;
            const start = this.carouselIndex % items.length;
            const slice = items.slice(start, start + count);
            if (slice.length < count) {
                return slice.concat(items.slice(0, count - slice.length));
            }
            return slice;
        },
        visibleCarouselItems() {
            return this.visibleCarouselTricks.map((trick) => ({
                id: trick.id,
                name: trick.name,
                thumbnail: this.getThumbnail(trick),
                trick,
            }));
        },
        carouselItemStyle() {
            const width = 100 / this.carouselVisibleCount;
            return { flex: `0 0 ${width}%` };
        },
        chartLevelStats() {
            return this.levels
                .map(level => ({
                    id: level.id,
                    label: level.name,
                    value: this.filteredData.filter(trick => Number(trick.level_id) === Number(level.id)).length,
                }))
                .filter(item => item.value > 0);
        },
        chartCategoryStats() {
            return this.categories
                .map(category => ({
                    id: category.id,
                    label: category.name,
                    value: this.filteredData.filter(trick => Number(trick.categorie_id) === Number(category.id)).length,
                }))
                .filter(item => item.value > 0);
        },
        chartLevelTotal() {
            return this.chartLevelStats.reduce((total, item) => total + item.value, 0);
        },
        chartCategoryTotal() {
            return this.chartCategoryStats.reduce((total, item) => total + item.value, 0);
        },
        tableLabels() {
            return {
                search: this.$t('trick_list.search'),
                newTrick: this.$t('trick_list.new_trick') || 'Nuevo truco',
                csvSync: this.$t('trick_list.csv_sync') || 'Comparar CSV',
                categoryFilter: this.$t('trick_list.category_filter'),
                levelFilter: this.$t('trick_list.level_filter'),
                name: this.$t('trick_list.name'),
                value: this.$t('trick_list.value'),
                level: this.$t('trick_list.level'),
                category: this.$t('trick_list.category'),
            };
        },
        chartLabels() {
            return {
                levelTitle: 'Niveles',
                categoryTitle: 'Categorias',
                total: 'Total',
                empty: 'No hay datos para mostrar',
            };
        },
        recommendationLabels() {
            const translate = (key, fallback) => {
                const value = this.$t(key);
                return value && value !== key ? value : fallback;
            };

            return {
                privateEyebrow: translate('trick_list.recommendations.private_eyebrow', 'Privado'),
                privateTitle: translate('trick_list.recommendations.private_title', 'Tus recomendaciones'),
                privateSubtitle: translate('trick_list.recommendations.private_subtitle', 'Propón trucos nuevos o cambios sobre la versión activa.'),
                publicEyebrow: translate('trick_list.recommendations.public_eyebrow', 'Participación pública'),
                publicTitle: translate('trick_list.recommendations.public_title', 'Estadísticas de participación'),
                publicSubtitle: translate('trick_list.recommendations.public_subtitle', 'Resumen agregado y detalle visible de las recomendaciones enviadas.'),
                newRecommendation: translate('trick_list.recommendations.new_recommendation', 'Nueva recomendación'),
                save: translate('trick_list.recommendations.save', 'Guardar recomendación'),
                saving: translate('trick_list.recommendations.saving', 'Guardando...'),
                cancel: translate('trick_list.recommendations.cancel', 'Cancelar'),
                delete: translate('trick_list.recommendations.delete', 'Eliminar'),
                deleting: translate('trick_list.recommendations.deleting', 'Eliminando...'),
                edit: translate('trick_list.recommendations.edit', 'Editar'),
                type: translate('trick_list.recommendations.type', 'Tipo'),
                newType: translate('trick_list.recommendations.new_type', 'Nuevo truco'),
                editType: translate('trick_list.recommendations.edit_type', 'Editar truco existente'),
                targetTrick: translate('trick_list.recommendations.target_trick', 'Truco objetivo'),
                selectTrick: translate('trick_list.recommendations.select_trick', 'Selecciona un truco'),
                searchTrick: translate('trick_list.recommendations.search_trick', 'Buscar truco por nombre, técnico o ID'),
                filteredResults: translate('trick_list.recommendations.filtered_results', 'resultados'),
                name: translate('trick_list.name', 'Nombre'),
                shortName: translate('trick_list.createModal.short_name', 'Nombre técnico'),
                description: translate('trick_list.createModal.description', 'Descripción'),
                category: translate('trick_list.category', 'Categoría'),
                level: translate('trick_list.level', 'Nivel'),
                endType: translate('trick_list.createModal.end_type', 'Finaliza a'),
                value: translate('trick_list.value', 'Valor'),
                detail: translate('trick_list.recommendations.detail', 'Detalle de la recomendación'),
                totalMine: translate('trick_list.recommendations.total_mine', 'Tus propuestas'),
                totalRecommendations: translate('trick_list.recommendations.total_recommendations', 'Total recomendaciones'),
                totalContributors: translate('trick_list.recommendations.total_contributors', 'Usuarios participantes'),
                totalDetails: translate('trick_list.recommendations.total_details', 'Detalles visibles'),
                byType: translate('trick_list.recommendations.by_type', 'Por tipo'),
                byStatus: translate('trick_list.recommendations.by_status', 'Por estado'),
                byCategory: translate('trick_list.recommendations.by_category', 'Por categoría'),
                byLevel: translate('trick_list.recommendations.by_level', 'Por nivel'),
                loading: translate('trick_list.recommendations.loading', 'Cargando recomendaciones...'),
                privateEmpty: translate('trick_list.recommendations.private_empty', 'Aún no has creado recomendaciones para esta versión.'),
                publicEmpty: translate('trick_list.recommendations.public_empty', 'Todavía no hay recomendaciones visibles para esta versión.'),
                pendingReview: translate('trick_list.recommendations.pending_review', 'Pendiente de revisión'),
                unresolvedVersion: translate('trick_list.recommendations.unresolved_version', 'Versión sin resolver'),
                version: translate('trick_list.recommendations.version', 'Versión'),
                createdAt: translate('trick_list.recommendations.created_at', 'Creada'),
                updatedAt: translate('trick_list.recommendations.updated_at', 'Actualizada'),
                notSpecified: translate('trick_list.recommendations.not_specified', 'No indicado'),
                untitled: translate('trick_list.recommendations.untitled', 'Sin título'),
                anonymous: translate('trick_list.recommendations.anonymous', 'Usuario'),
                publicSlider: translate('trick_list.recommendations.public_slider', 'Deslizar recomendaciones'),
                viewDetail: translate('trick_list.recommendations.view_detail', 'Ver detalle'),
                moreChanges: translate('trick_list.recommendations.more_changes', 'cambios'),
                changeSummary: translate('trick_list.recommendations.change_summary', 'Cambios detectados'),
                originalValue: translate('trick_list.recommendations.original_value', 'Original'),
                proposedValue: translate('trick_list.recommendations.proposed_value_label', 'Propuesto'),
                proposedData: translate('trick_list.recommendations.proposed_data', 'Datos propuestos'),
                noChangesDetected: translate('trick_list.recommendations.no_changes_detected', 'No se detectaron cambios comparables'),
                selectedTrickLabel: translate('trick_list.recommendations.selected_trick_label', 'Seleccionado'),
                clearSelection: translate('trick_list.recommendations.clear_selection', 'Cambiar'),
                noTrickResults: translate('trick_list.recommendations.no_trick_results', 'No se encontraron trucos con esa búsqueda'),
                recommendFromTrick: translate('trick_list.recommendations.recommend_from_trick', 'Recomendar cambio sobre este truco'),
            };
        }
    },
    methods: {
        resolveInteger(value) {
            const parsed = Number(value);
            return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
        },
        buildRecommendationContext() {
            const query = this.$route?.query || {};
            return {
                round_id: this.resolveInteger(query.round_id),
                competition_id: this.resolveInteger(query.competition_id),
                tricklist_version_id: this.resolveInteger(query.tricklist_version_id),
            };
        },
        recommendationRequestParams() {
            return Object.fromEntries(Object.entries(this.recommendationContext).filter(([, value]) => value));
        },
        async initializeRecommendationCards() {
            this.recommendationContext = this.buildRecommendationContext();
            await Promise.all([
                this.fetchPrivateRecommendations(),
                this.fetchPublicRecommendationSummary(),
            ]);
        },
        assignResolvedRecommendationVersion(version) {
            if (version && typeof version === 'object') {
                this.recommendationVersion = version;
                if (version.id) {
                    this.recommendationContext.tricklist_version_id = Number(version.id);
                }
            }
        },
        async fetchPrivateRecommendations() {
            this.recommendationLoading = true;
            this.recommendationPrivateError = '';
            try {
                const response = await this.$axios.get('/trick-recommendations/mine', {
                    headers: { ...this.getAuthHeader() },
                    params: this.recommendationRequestParams(),
                });
                this.privateRecommendations = Array.isArray(response.data?.recommendations) ? response.data.recommendations : [];
                this.assignResolvedRecommendationVersion(response.data?.tricklist_version || null);
            } catch (error) {
                this.privateRecommendations = [];
                this.recommendationPrivateError = error?.response?.data?.message || 'No se pudieron cargar tus recomendaciones';
            } finally {
                this.recommendationLoading = false;
            }
        },
        async fetchPublicRecommendationSummary() {
            this.recommendationPublicLoading = true;
            this.recommendationPublicError = '';
            try {
                const response = await this.$axios.get('/trick-recommendations/public-summary', {
                    headers: { ...this.getAuthHeader() },
                    params: this.recommendationRequestParams(),
                });
                this.publicRecommendationSummary = response.data?.summary || {
                    total: 0,
                    contributors: 0,
                    by_type: [],
                    by_status: [],
                    by_category: [],
                    by_level: [],
                };
                this.publicRecommendations = Array.isArray(response.data?.recommendations) ? response.data.recommendations : [];
                this.assignResolvedRecommendationVersion(response.data?.tricklist_version || null);
            } catch (error) {
                this.publicRecommendationSummary = {
                    total: 0,
                    contributors: 0,
                    by_type: [],
                    by_status: [],
                    by_category: [],
                    by_level: [],
                };
                this.publicRecommendations = [];
                this.recommendationPublicError = error?.response?.data?.message || 'No se pudo cargar la participación pública';
            } finally {
                this.recommendationPublicLoading = false;
            }
        },
        normalizeRecommendationPayload(payload) {
            const normalizeNullable = (value) => {
                if (value === '' || value === undefined) {
                    return null;
                }

                return value;
            };

            return {
                ...this.recommendationRequestParams(),
                recommendation_type: payload.recommendation_type,
                trick_id: payload.recommendation_type === 'edit_existing_trick' ? normalizeNullable(payload.trick_id) : null,
                proposed_name: normalizeNullable(payload.proposed_name),
                proposed_short_name: normalizeNullable(payload.proposed_short_name),
                proposed_description: normalizeNullable(payload.proposed_description),
                proposed_categorie_id: normalizeNullable(payload.proposed_categorie_id),
                proposed_level_id: normalizeNullable(payload.proposed_level_id),
                proposed_type_id: normalizeNullable(payload.proposed_type_id),
                proposed_value: normalizeNullable(payload.proposed_value),
                detail: payload.detail === undefined ? '' : payload.detail,
            };
        },
        async submitRecommendation({ id, payload, onSuccess }) {
            this.recommendationSaving = true;
            this.recommendationPrivateError = '';
            try {
                const endpoint = id ? `/trick-recommendations/${id}` : '/trick-recommendations';
                const method = id ? 'patch' : 'post';
                await this.$axios[method](endpoint, this.normalizeRecommendationPayload(payload), {
                    headers: { ...this.getAuthHeader() },
                });
                if (typeof onSuccess === 'function') {
                    onSuccess();
                }
                await Promise.all([
                    this.fetchPrivateRecommendations(),
                    this.fetchPublicRecommendationSummary(),
                ]);
            } catch (error) {
                this.recommendationPrivateError = error?.response?.data?.message || 'No se pudo guardar la recomendación';
            } finally {
                this.recommendationSaving = false;
            }
        },
        async deleteRecommendation(id) {
            if (!id) return;
            this.recommendationDeletingId = id;
            this.recommendationPrivateError = '';
            try {
                await this.$axios.delete(`/trick-recommendations/${id}`, {
                    headers: { ...this.getAuthHeader() },
                });
                await Promise.all([
                    this.fetchPrivateRecommendations(),
                    this.fetchPublicRecommendationSummary(),
                ]);
            } catch (error) {
                this.recommendationPrivateError = error?.response?.data?.message || 'No se pudo eliminar la recomendación';
            } finally {
                this.recommendationDeletingId = null;
            }
        },
        applyRecommendationVoteUpdate(recommendationId, votes) {
            const mergeVotes = (recommendations) => (recommendations || []).map((recommendation) => {
                if (recommendation.id !== recommendationId) {
                    return recommendation;
                }

                return {
                    ...recommendation,
                    votes: {
                        ...(recommendation.votes || {}),
                        ...votes,
                    },
                };
            });

            this.publicRecommendations = mergeVotes(this.publicRecommendations);
            this.privateRecommendations = mergeVotes(this.privateRecommendations);
        },
        async voteRecommendation({ recommendationId, vote }) {
            if (!recommendationId) return;
            this.recommendationVoteBusyId = recommendationId;
            this.recommendationPublicError = '';
            try {
                const response = await this.$axios.post(`/trick-recommendations/${recommendationId}/vote`, { vote }, {
                    headers: { ...this.getAuthHeader() },
                });

                if (response.data?.votes) {
                    this.applyRecommendationVoteUpdate(recommendationId, response.data.votes);
                }
            } catch (error) {
                this.recommendationPublicError = error?.response?.data?.message || 'No se pudo registrar el voto';
            } finally {
                this.recommendationVoteBusyId = null;
            }
        },
        openPublicRecommendationDetail(recommendation) {
            if (!recommendation) {
                return;
            }

            this.selectedPublicRecommendation = recommendation;
            this.$nextTick(() => {
                const modalEl = document.getElementById('publicRecommendationDetailModal');
                if (modalEl) {
                    Modal.getOrCreateInstance(modalEl).show();
                }
            });
        },
        isEditRecommendation(recommendation) {
            return recommendation?.recommendation_type === 'edit_existing_trick';
        },
        publicRecommendationChangedFields(recommendation) {
            return Array.isArray(recommendation?.changed_fields) ? recommendation.changed_fields : [];
        },
        formatRecommendationFieldValue(value) {
            if (value === null || value === undefined || value === '') {
                return this.recommendationLabels.notSpecified;
            }

            return String(value);
        },
        formatRecommendationDate(value) {
            if (!value) {
                return this.recommendationLabels.notSpecified;
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return this.recommendationLabels.notSpecified;
            }

            return date.toLocaleString();
        },
        publicRecommendationDetailFields(recommendation) {
            return [
                {
                    label: this.recommendationLabels.name,
                    value: this.formatRecommendationFieldValue(recommendation?.proposed_name),
                },
                {
                    label: this.recommendationLabels.shortName,
                    value: this.formatRecommendationFieldValue(recommendation?.proposed_short_name),
                },
                {
                    label: this.recommendationLabels.category,
                    value: this.formatRecommendationFieldValue(recommendation?.proposed_category?.name),
                },
                {
                    label: this.recommendationLabels.level,
                    value: this.formatRecommendationFieldValue(recommendation?.proposed_level?.name),
                },
                {
                    label: this.recommendationLabels.value,
                    value: this.formatRecommendationFieldValue(recommendation?.proposed_value),
                },
                {
                    label: this.recommendationLabels.endType,
                    value: this.formatRecommendationFieldValue(recommendation?.proposed_type?.name),
                },
                {
                    label: this.recommendationLabels.description,
                    value: this.formatRecommendationFieldValue(recommendation?.proposed_description),
                },
            ].filter((field) => field.value !== this.recommendationLabels.notSpecified);
        },

        getSummaryChartCanvas(refName) {
            return this.$refs.summaryCharts?.$refs?.[refName] || null;
        },

        destroySummaryCharts(invalidatePending = true) {
            if (invalidatePending) {
                this.summaryChartRenderNonce += 1;
            }

            if (this.trickLevelSummaryChartInstance) {
                this.trickLevelSummaryChartInstance.destroy();
                this.trickLevelSummaryChartInstance = null;
            }

            if (this.trickCategorySummaryChartInstance) {
                this.trickCategorySummaryChartInstance.destroy();
                this.trickCategorySummaryChartInstance = null;
            }
        },
        getChartContext(canvasRef) {
            if (!canvasRef || typeof canvasRef.getContext !== 'function') {
                return null;
            }

            if ('isConnected' in canvasRef && !canvasRef.isConnected) {
                return null;
            }

            return canvasRef.getContext('2d');
        },
        renderSummaryCharts() {
            const currentNonce = this.summaryChartRenderNonce + 1;
            this.summaryChartRenderNonce = currentNonce;

            this.$nextTick(() => {
                if (currentNonce !== this.summaryChartRenderNonce) {
                    return;
                }

                this.destroySummaryCharts(false);

                if (currentNonce !== this.summaryChartRenderNonce) {
                    return;
                }

                const levelContext = this.getChartContext(this.getSummaryChartCanvas('levelCanvas'));
                const categoryContext = this.getChartContext(this.getSummaryChartCanvas('categoryCanvas'));
                const legendLabelConfig = {
                    boxWidth: this.isMobile ? 10 : 12,
                    boxHeight: this.isMobile ? 10 : 12,
                    padding: this.isMobile ? 10 : 14,
                    font: {
                        size: this.isMobile ? 10 : 12,
                    },
                    color: '#374151',
                };

                if (levelContext && this.chartLevelStats.length) {
                    this.trickLevelSummaryChartInstance = new Chart(levelContext, {
                        type: 'doughnut',
                        data: {
                            labels: this.chartLevelStats.map(item => item.label),
                            datasets: [{
                                data: this.chartLevelStats.map(item => item.value),
                                backgroundColor: ['#084cff', '#17e700', '#ff7e00', '#ff0000'],
                                borderWidth: 0,
                            }],
                        },
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            animation: false,
                            cutout: this.isMobile ? '52%' : '60%',
                            plugins: {
                                legend: {
                                    position: 'bottom',
                                    labels: legendLabelConfig,
                                },
                            },
                        },
                    });
                }

                if (categoryContext && this.chartCategoryStats.length) {
                    this.trickCategorySummaryChartInstance = new Chart(categoryContext, {
                        type: 'doughnut',
                        data: {
                            labels: this.chartCategoryStats.map(item => item.label),
                            datasets: [{
                                data: this.chartCategoryStats.map(item => item.value),
                                backgroundColor: ['#0ea5e9', '#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#22c55e', '#64748b', '#f97316', '#4f46e5', '#84cc16'],
                                borderWidth: 0,
                            }],
                        },
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            animation: false,
                            cutout: this.isMobile ? '52%' : '60%',
                            plugins: {
                                legend: {
                                    position: 'bottom',
                                    labels: legendLabelConfig,
                                },
                            },
                        },
                    });
                }
            });
        },

        requestDeleteVideo() {
            if (!this.isSupervisorUser) return;
            const candidate = this.selectedVideo;
            if (!candidate) return;
            this.videoToDelete = candidate;
            this.deleteError = '';
            const modalEl = document.getElementById('confirmDeleteVideoModal');
            if (modalEl) {
                Modal.getOrCreateInstance(modalEl).show();
            }
        },
        async confirmDeleteVideo() {
            if (!this.videoToDelete || !this.isSupervisorUser) return;
            const videoId = this.videoToDelete.id || this.videoToDelete.video_id;
            if (!videoId) return;
            this.deleteLoading = true;
            this.deleteError = '';
            try {
                await this.$axios.delete(`/trick-videos/${videoId}`, {
                    headers: {
                        ...this.getAuthHeader(),
                    }
                });

                const stripVideo = (videos) => Array.isArray(videos)
                    ? videos.filter(v => (v.id || v.video_id) !== videoId)
                    : [];

                if (this.selectedTrick) {
                    this.selectedTrick = { ...this.selectedTrick, videos: stripVideo(this.selectedTrick.videos) };
                }
                this.yourData = (this.yourData || []).map(t => t.id === this.selectedTrick?.id ? { ...t, videos: stripVideo(t.videos) } : t);
                secureStorage.setItem('tricks', JSON.stringify(this.yourData));

                this.youtubeLink = this.selectedVideoUrl;
                localStorage.removeItem('youtubeLink');
                const modalEl = document.getElementById('confirmDeleteVideoModal');
                if (modalEl) {
                    Modal.getOrCreateInstance(modalEl).hide();
                }
            } catch (error) {
                this.deleteError = error?.response?.data?.message || 'No se pudo eliminar el video';
            } finally {
                this.deleteLoading = false;
                this.videoToDelete = null;
            }
        },
        handleVideo(trick) {
            this.trickId = trick.id;
            if (trick.video_url){
                this.youtubeLink = trick.video_url;
            } else {
                this.youtubeLink = null;
                this.uploadFile = null;
                this.uploadError = '';
                this.uploadProgress = 0;
                this.uploadSuccess = '';
            }
        },
        onModalHidden() {
            this.youtubeLink = null;
            localStorage.removeItem('youtubeLink');
        },
        openTrickFromCarousel(trick) {
            if (!trick) {
                return;
            }
            this.viewDetails(trick);
            this.showInfoModal();
            this.scheduleAutoSlide();
        },
        async openCreateModal() {
            if (!this.isSupervisorUser) return;
            await this.fetchSelectors();
            this.resetCreateForm();
            const modalEl = document.getElementById('createTrickModal');
            if (modalEl) {
                const modal = Modal.getOrCreateInstance(modalEl);
                modal.show();
            }
        },
        showInfoModal() {
            const modalEl = document.getElementById('infoModal');
            if (modalEl) {
                const modal = Modal.getOrCreateInstance(modalEl);
                modal.show();
            }
        },
        cleanupModalArtifacts() {
            document.body.classList.remove('modal-open');
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
            document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
        },
        async openRecommendationFromSelectedTrick() {
            if (!this.selectedTrick?.id) {
                return;
            }

            await this.fetchSelectors();

            const openEditor = () => {
                this.$refs.privateRecommendationCard?.openForExistingTrick?.(this.selectedTrick);
                this.$nextTick(() => {
                    this.$refs.privateRecommendationSection?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                });
            };

            const modalEl = document.getElementById('infoModal');
            if (!modalEl) {
                openEditor();
                return;
            }

            const modal = Modal.getInstance(modalEl) || Modal.getOrCreateInstance(modalEl);
            if (!modalEl.classList.contains('show')) {
                this.cleanupModalArtifacts();
                openEditor();
                return;
            }

            modalEl.addEventListener('hidden.bs.modal', () => {
                this.cleanupModalArtifacts();
                openEditor();
            }, { once: true });
            modal.hide();
        },
        viewDetails(trick) {
            if (!trick) {
                return;
            }
            this.selectedTrick = trick;
            this.trickId = trick.id;
            this.uploadFile = null;
            this.uploadError = '';
            this.uploadProgress = 0;
            this.uploadSuccess = '';
            if (this.hasRotationToggle) {
                this.selectedRotation = this.frontVideo ? 1 : 2;
            } else {
                this.selectedRotation = null;
            }
            this.uploadRotation = this.missingRotation;
            this.youtubeLink = this.selectedVideoUrl;
        },
        handleRealtimeTricklistUpdate() {
            const nextData = JSON.parse(secureStorage.getItem('tricks')) || [];
            this.yourData = nextData;

            if (this.selectedTrick?.id) {
                this.selectedTrick = nextData.find(trick => Number(trick?.id) === Number(this.selectedTrick.id)) || null;
                this.youtubeLink = this.selectedVideoUrl;
            }

            this.$nextTick(() => {
                this.renderSummaryCharts();
            });
        },
        selectRotation(rotation) {
            this.selectedRotation = rotation;
            if (this.selectedVideoUrl === null && this.hasRotationToggle && this.missingRotation) {
                this.uploadRotation = this.missingRotation;
            }
            this.youtubeLink = this.selectedVideoUrl;
            this.scheduleAutoSlide();
        },
        getFirstVideoUrl(trick) {
            if (!Array.isArray(trick?.videos) || trick.videos.length === 0) return null;
            const front = trick.videos.find(v => v.rotation === 1);
            const back = trick.videos.find(v => v.rotation === 2);
            const single = trick.videos.find(v => v.rotation === null || v.rotation === undefined);
            return (front || back || single || trick.videos[0]).video_url || null;
        },
        getLatestVideoDate(trick) {
            if (!Array.isArray(trick?.videos) || trick.videos.length === 0) return 0;
            const dates = trick.videos
                .map(v => v?.created_at || v?.updated_at)
                .filter(Boolean)
                .map(d => new Date(d).getTime())
                .filter(ts => !Number.isNaN(ts));
            if (dates.length === 0) return 0;
            return Math.max(...dates);
        },
        getVideoIdFromUrl(url) {
            const regex = /[?&]v=([^?&]+)/;
            const match = url?.match(regex);
            if (match && match[1]) return match[1];
            const shortFormatRegex = /youtu\.be\/([^?&]+)/;
            const shortFormatMatch = url?.match(shortFormatRegex);
            return shortFormatMatch && shortFormatMatch[1] ? shortFormatMatch[1] : '';
        },
        getThumbnail(trick) {
            const url = this.getFirstVideoUrl(trick);
            if (!url) return this.videoOffImage;
            const id = this.getVideoIdFromUrl(url);
            return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : this.videoOffImage;
        },
        async fetchSelectors() {
            if (this.selectorCategories.length && this.selectorLevels.length && this.selectorTypes.length) return;
            try {
                const resp = await this.$axios.get('/tricks/selectors', { headers: { ...this.getAuthHeader() } });
                const cats = resp.data?.categories || resp.data?.data?.categories || [];
                const lvls = resp.data?.levels || resp.data?.data?.levels || [];
                const types = resp.data?.types || resp.data?.data?.types || [];
                this.selectorCategories = Array.isArray(cats) && cats.length ? cats : this.categories;
                this.selectorLevels = Array.isArray(lvls) && lvls.length ? lvls : this.levels;
                this.selectorTypes = Array.isArray(types) ? types : [];
            } catch (e) {
                this.selectorCategories = this.categories;
                this.selectorLevels = this.levels;
                this.selectorTypes = [];
            }
        },
        resetCreateForm() {
            this.createForm = {
                name: '',
                short_name: '',
                description: '',
                categorie_id: null,
                level_id: null,
                value: null,
                type_id: null,
            };
            this.createError = '';
        },
        async createTrick() {
            if (this.createLoading) return;
            if (!this.createForm.name || !this.createForm.categorie_id || !this.createForm.level_id) {
                this.createError = this.$t('trick_list.createModal.required') || 'Nombre, categoría y nivel son obligatorios';
                return;
            }
            this.createLoading = true;
            this.createError = '';
            try {
                const payload = {
                    name: this.createForm.name,
                    categorie_id: this.createForm.categorie_id,
                    level_id: this.createForm.level_id,
                    short_name: this.createForm.short_name || undefined,
                    description: this.createForm.description || undefined,
                    value: this.createForm.value || undefined,
                    type_id: this.createForm.type_id ?? null,
                };
                const resp = await this.$axios.post('/tricks', payload, { headers: { ...this.getAuthHeader() } });
                const newTrick = resp.data?.trick || resp.data;
                if (newTrick) {
                    this.yourData = [newTrick, ...this.yourData];
                    secureStorage.setItem('tricks', JSON.stringify(this.yourData));
                }
                const modalEl = document.getElementById('createTrickModal');
                if (modalEl) {
                    Modal.getOrCreateInstance(modalEl).hide();
                }
            } catch (error) {
                this.createError = error?.response?.data?.message || 'No se pudo crear el truco';
            } finally {
                this.createLoading = false;
            }
        },
        openCsvSyncView() {
            if (!this.isSupervisorUser) return;
            this.$router.push('/trickList/csv-sync');
        },
        nextCarousel() {
            if (!this.hasCarousel) return;
            this.carouselIndex = (this.carouselIndex + 1) % this.activeVideoTricks.length;
            this.scheduleAutoSlide();
        },
        prevCarousel() {
            if (!this.hasCarousel) return;
            this.carouselIndex = (this.carouselIndex - 1 + this.activeVideoTricks.length) % this.activeVideoTricks.length;
            this.scheduleAutoSlide();
        },
        scheduleAutoSlide() {
            if (this.autoSlideTimer) {
                clearTimeout(this.autoSlideTimer);
            }
            if (!this.hasCarousel) return;
            this.autoSlideTimer = setTimeout(() => {
                this.nextCarousel();
            }, this.autoSlideDelay);
        },
        handleResize() {
            this.isMobile = window.innerWidth < 576;
            this.scheduleAutoSlide();
            this.renderSummaryCharts();
        },
        capitalizeFirstLetter(selectedTrick) {
            let levelName = this.levelName(selectedTrick.level_id);
            if (typeof levelName !== 'string') return '';
            return levelName
                .split('/')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' / ');
        },
        categoryName(id) {
            const categories = [
                { id: 1, name: 'Aerial'},
                { id: 2, name: 'Aerial / Flip'},
                { id: 3, name: 'Aerial / Switch'},
                { id: 4, name: 'Dynamic'},
                { id: 5, name: 'Dynamic / Flip'},
                { id: 6, name: 'Dynamic / Flip / Switch '},
                { id: 7, name: 'Dynamic / Switch'},
                { id: 8, name: 'Dynamic / Transition'},
                { id: 9, name: 'Mount'},
                { id: 10, name: 'Static'},
                { id: 11, name: 'Transition'},
            ];
            const category = categories.find(category => category.id === id);

            return category ? category.name : '';
        },
        levelName(id) {
            const levels = [
                { id: 1, name: 'ROOKY'},
                { id: 2, name: 'AMATEUR'},
                { id: 3, name: 'PRO'},
                { id: 4, name: 'LEGENDARY'},
            ];

            const level = levels.find(level => level.id === id);

            return level ? level.name : '';
        },
        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
            }
        },
        sortBy(key) {
            this.sortKey = key;
            this.sortOrders[key] = this.sortOrders[key] * -1;
            this.filterCategory = '';
            this.filterLevel = '';
        },
        hasVideoLink(trick) {
            const directVideoUrl = typeof trick?.video_url === 'string' ? trick.video_url.trim() : '';
            if (directVideoUrl) {
                return true;
            }

            return Array.isArray(trick?.videos)
                && trick.videos.some(video => typeof video?.video_url === 'string' && video.video_url.trim() !== '');
        },
        getTrickLevelSortValue(trick) {
            const levelValue = Number(trick?.level_id ?? trick?.level ?? 0);
            return Number.isFinite(levelValue) ? levelValue : 0;
        },
        defaultSortData(a, b) {
            const videoPriority = Number(this.hasVideoLink(b)) - Number(this.hasVideoLink(a));
            if (videoPriority !== 0) {
                return videoPriority;
            }

            const levelPriority = this.getTrickLevelSortValue(b) - this.getTrickLevelSortValue(a);
            if (levelPriority !== 0) {
                return levelPriority;
            }

            return Number(a?.id || 0) - Number(b?.id || 0);
        },
        getSortValue(trick, key) {
            switch (key) {
                case 'level':
                    return Number(trick?.level_id || 0);
                case 'category':
                    return this.categoryName(Number(trick?.categorie_id || 0)).toLowerCase();
                case 'id':
                case 'value':
                    return Number(trick?.[key] || 0);
                case 'name':
                    return String(trick?.name || '').toLowerCase();
                default:
                    return trick?.[key] ?? '';
            }
        },
        sortData(a, b) {
            if (!this.sortKey) {
                return this.defaultSortData(a, b);
            }

            const order = this.sortOrders[this.sortKey];
            const valueA = this.getSortValue(a, this.sortKey);
            const valueB = this.getSortValue(b, this.sortKey);

            if (valueA < valueB) {
                return -1 * order;
            } else if (valueA > valueB) {
                return 1 * order;
            } else {
                return 0;
            }
        },
        resetFilter(filterName) {
            this[filterName] = '';
        },
        getBackgroundColorClass(level) {
            switch (Number(level)) {
                case 1:
                    return 'color-level-1';
                case 2:
                    return 'color-level-2';
                case 3:
                    return 'color-level-3';
                case 4:
                    return 'color-level-4';
                default:
                    return '';
            }
        },
        onFileSelected(event) {
            const file = event.target.files?.[0];
            this.uploadFile = file || null;
            this.uploadError = '';
            this.uploadProgress = 0;
            this.uploadSuccess = '';
        },
        async uploadVideo() {
            if (!this.uploadFile || !this.trickId) return;
            this.uploading = true;
            this.uploadError = '';
            this.uploadProgress = 0;
            this.uploadSuccess = '';
            const uploadId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
            this.uploadId = uploadId;
            const file = this.uploadFile;
            const totalChunks = Math.ceil(file.size / this.CHUNK_SIZE) || 1;
            this.totalChunks = totalChunks;
            let rotationToSend = null;
            if (this.missingRotation && this.selectedVideoUrl === null && this.hasRotationToggle) {
                rotationToSend = this.missingRotation;
            } else if (this.videos.length === 0) {
                rotationToSend = this.uploadRotation;
            }
            const rotationParam = rotationToSend !== null && rotationToSend !== undefined ? Number(rotationToSend) : null;
            try {
                for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
                    const start = chunkIndex * this.CHUNK_SIZE;
                    const end = Math.min(start + this.CHUNK_SIZE, file.size);
                    const blob = file.slice(start, end);

                    const formData = new FormData();
                    formData.append('trick_id', this.trickId);
                    formData.append('upload_id', uploadId);
                    formData.append('chunk_index', chunkIndex);
                    formData.append('total_chunks', totalChunks);
                    if (rotationParam !== null) {
                        formData.append('rotation', rotationParam);
                    }
                    formData.append('video', blob, file.name);

                    const response = await this.$axios.post('/tricks/upload-chunk', formData, {
                        headers: {
                            ...this.getAuthHeader(),
                            'Content-Type': 'multipart/form-data'
                        }
                    });

                    if (response.status !== 200) {
                        throw new Error(this.$t('competitions.messages.upload_failed') || 'Upload failed');
                    }
                    this.uploadProgress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
                }

                const finalizeBody = {
                    trick_id: this.trickId,
                    upload_id: uploadId,
                    total_chunks: totalChunks
                };
                if (rotationParam !== null) {
                    finalizeBody.rotation = rotationParam;
                }

                const finalize = await this.$axios.post('/tricks/complete-upload', finalizeBody, {
                    headers: {
                        ...this.getAuthHeader()
                    }
                });

                if (finalize.status === 200) {
                    const updated = finalize.data?.trick;
                    if (updated) {
                        this.yourData = this.yourData.map(t => t.id === updated.id ? { ...t, ...updated } : t);
                        secureStorage.setItem('tricks', JSON.stringify(this.yourData));
                        if (this.selectedTrick?.id === updated.id) {
                            this.selectedTrick = { ...this.selectedTrick, ...updated };
                            if (this.hasRotationToggle) {
                                this.selectedRotation = this.frontVideo ? 1 : 2;
                            } else {
                                this.selectedRotation = null;
                            }
                            this.uploadRotation = this.missingRotation;
                            this.youtubeLink = this.selectedVideoUrl;
                        }
                    }
                    this.uploadFile = null;
                    this.uploadSuccess = this.$t('trick_list.videoInsertModal.success') || 'Video subido con éxito';
                } else {
                    this.uploadError = finalize.data?.missing ? `Faltan chunks: ${finalize.data.missing}` : (this.$t('competitions.messages.upload_failed') || 'Upload failed');
                }
            } catch (error) {
                this.uploadError = error?.response?.data?.message || 'Upload failed';
            } finally {
                this.uploading = false;
            }
        },
        getAuthHeader() {
            // Prefer Vuex token, fallback to secureStorage, then store getter
            const fromState = this.token;
            const fromSecure = secureStorage.getItem('token');
            const cleanedSecure = fromSecure ? fromSecure.replace(/"/g, '') : null;
            const legacy = this.$store?.getters?.getToken;
            const token = fromState || cleanedSecure || legacy;
            return token ? { Authorization: `Bearer ${token}` } : {};
        },
        async deleteYoutubeLinkInTrick(id) {
            try {
                const response = await this.$axios.delete(`/trick/${id}`, {
                    headers: {
                        Authorization: `Bearer ${this.$store.getters.getToken}`,
                    },
                });
                console.log(response);
                this.hideLoading();
            } catch (error) {
                console.error('Error al eliminar el registro:', error.response ? error.response.data : error.message);
            }

        },
        showLoading() {
            this.$nextTick(() => {
                this.$refs.loadingModal.showLoading();
            });
        },
        hideLoading() {
            this.$nextTick(() => {
                this.$refs.loadingModal.hideLoading();
            });
        },

    },
    watch: {
        searchQuery() {
            this.currentPage = 1;
        },
        filteredData() {
            this.renderSummaryCharts();
        },
        '$route.query': {
            deep: true,
            handler() {
                this.initializeRecommendationCards();
            },
        },
    }
};
</script>

<style scoped>
.table th {
    white-space: nowrap;
}

.table th i {
    margin-left: 5px;
    vertical-align: middle;
}
.table td {
    vertical-align: middle;
}
.table tbody tr {
    cursor: pointer;
}
.pagination {
    margin: 0;  /* Elimina el margen */
}
.custom-active {
    background-color: #6c757d;
    border-color: #6c757d;
    color: #fff;
}
.table th:nth-child(1),
.table th:nth-child(2),
.table th:nth-child(3),
.table th:nth-child(4),
.table th:nth-child(5){
    cursor: pointer;
}

.modal-body {
    padding: 10px;
}

.trick-carousel {
    background-color: #f8f9fa;
}

.trick-summary-charts {
    background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
}

.trick-layout {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.recommendation-section {
    margin-top: 12px;
}

.trick-list-panel,
.trick-summary-charts {
    min-width: 0;
}

.trick-list-content {
    max-width: 100%;
}

.summary-chart-card {
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    background: #ffffff;
    padding: 16px;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.summary-chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.summary-chart-body {
    position: relative;
    min-height: 300px;
}

.summary-chart-body canvas {
    width: 100%;
    height: 100%;
}

.summary-chart-canvas-hidden {
    visibility: hidden;
}

.summary-chart-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #6b7280;
    text-align: center;
    left: 0;
    padding: 0 12px;
    position: absolute;
    top: 0;
    width: 100%;
}

@media (min-width: 1200px) {
    .trick-layout {
        display: grid;
        grid-template-columns: minmax(280px, 0.8fr) minmax(0, 1.75fr);
        align-items: start;
        gap: 24px;
    }

    .trick-summary-charts {
        grid-column: 1;
    }

    .trick-list-panel {
        grid-column: 2;
    }

    .trick-summary-charts {
        position: sticky;
        top: 16px;
        margin-bottom: 0;
    }

    .summary-chart-body {
        min-height: 240px;
    }

    .summary-chart-empty {
        min-height: 240px;
    }
}

.carousel-track {
    display: flex;
    gap: 10px;
    overflow: hidden;
}

.carousel-item-thumb {
    cursor: pointer;
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 6px;
    background: #fff;
    transition: transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.carousel-item-thumb:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.thumb-image-wrapper {
    position: relative;
    width: 100%;
    padding-top: 56.25%;
    overflow: hidden;
    border-radius: 4px;
    background: #000;
}

.thumb-image-wrapper img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.thumb-title {
    font-size: 0.85rem;
    margin-top: 4px;
}

.info-row {
    margin-bottom: 15px;
    display: flex;
}

.info-row--action {
    justify-content: flex-end;
}

.label {
    flex: 1;
    font-weight: bold;
    margin-right: 0;
}

.value {
    flex: 2;
}

.level-circle {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid #000;
}

.public-recommendation-modal__eyebrow {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #c2410c;
    font-weight: 700;
}

.public-recommendation-modal__body {
    padding: 18px;
}

.public-recommendation-modal__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 18px;
    color: #64748b;
    font-size: 0.9rem;
    margin-bottom: 18px;
}

.public-recommendation-modal__section + .public-recommendation-modal__section {
    margin-top: 20px;
}

.public-recommendation-modal__section h6 {
    margin-bottom: 12px;
    font-weight: 700;
}

.public-recommendation-modal__changes {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.public-recommendation-modal__change-card {
    border: 1px solid #fed7aa;
    border-radius: 14px;
    background: #fff7ed;
    padding: 14px;
}

.public-recommendation-modal__change-label {
    display: inline-flex;
    margin-bottom: 10px;
    font-weight: 700;
    color: #9a3412;
}

.public-recommendation-modal__change-values {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    gap: 12px;
    align-items: center;
}

.public-recommendation-modal__change-values small {
    display: block;
    color: #64748b;
    margin-bottom: 4px;
}

.public-recommendation-modal__change-values strong {
    display: block;
    word-break: break-word;
}

.public-recommendation-modal__change-arrow {
    font-size: 1.25rem;
    color: #c2410c;
    font-weight: 700;
}

.public-recommendation-modal__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 14px;
    margin: 0;
}

.public-recommendation-modal__grid dt {
    font-size: 0.8rem;
    color: #64748b;
}

.public-recommendation-modal__grid dd {
    margin: 4px 0 0;
    font-weight: 600;
    word-break: break-word;
}

.with-borders {
    border-top: 1px solid #000000;
    border-bottom: 1px solid #000000;
    border-right: 2px solid #000000;
    border-left: 2px solid #000000;
    padding: 0;
    text-align: center;
}
.color-level-1 {
    color: #ffffff;
    background-color: rgb(8, 76, 255);
}
.color-level-2 {
    color: #ffffff;
    background-color: rgb(23, 231, 0);
}
.color-level-3 {
    color: #ffffff;
    background-color: rgb(255, 126, 0);
}
.color-level-4 {
    color: #ffffff;
    background-color: rgb(255, 0, 0);
}
@media (max-width: 576px) {
    .summary-chart-body {
        min-height: 260px;
    }

    .summary-chart-empty {
        min-height: 260px;
    }

    .table th:nth-child(4),
    .table td:nth-child(4),
    .table th:nth-child(5),
    .table td:nth-child(5) {
        display: none;
    }

    .public-recommendation-modal__change-values {
        grid-template-columns: 1fr;
    }

    .public-recommendation-modal__change-arrow {
        display: none;
    }
}
</style>
